import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  Check,
  Clipboard,
  KeyRound,
  Lock,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCog,
  UserPlus,
  X,
} from "lucide-react";
import { AppShell } from "@client/components/app-shell";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Badge } from "@client/components/ui/badge";
import { Spinner } from "@client/components/ui/spinner";
import { toast } from "@client/components/ui/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@client/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";
import { cn } from "@client/lib/cn";

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "lawyer" | "paralegal";
  oabNumber: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  deletedAt: string | null;
}

interface CreateUserInput {
  email: string;
  nome: string;
  role: "admin" | "lawyer" | "paralegal";
  senha: string;
  oabNumber?: string;
}

async function fetchUsers(): Promise<{ users: User[] }> {
  const res = await fetch("/api/admin/users", { credentials: "include" });
  if (!res.ok) throw new Error("Erro ao listar usuários");
  return res.json();
}

async function createUser(input: CreateUserInput): Promise<{ user: User }> {
  const res = await fetch("/api/admin/users", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 409) throw new Error("Já existe usuário com este email.");
    if (res.status === 400) {
      const first = body?.issues?.[0];
      throw new Error(first?.message ?? "Dados inválidos.");
    }
    throw new Error(body?.error ?? `Erro ao criar (${res.status}).`);
  }
  return res.json();
}

async function deleteUser(id: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? "Erro ao remover usuário");
  }
}

export const Route = createFileRoute("/admin/users")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) throw redirect({ to: "/login" });
    const body = await res.json();
    if (body.user.role !== "admin") throw redirect({ to: "/dashboard" });
  },
  component: AdminUsersPage,
});

function generateTempPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789abcdefghjkmnpqrstuvwxyz";
  const symbols = "!@#$%&*";
  let pwd = "";
  for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  pwd += symbols[Math.floor(Math.random() * symbols.length)];
  pwd += Math.floor(Math.random() * 100);
  return pwd;
}

function formatDateBR(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return "Nunca acessou";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Agora há pouco";
  if (diffMin < 60) return `Há ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `Há ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return "Ontem";
  if (diffD < 30) return `Há ${diffD} dias`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function roleBadge(role: User["role"]) {
  const config = {
    admin: { variant: "gold" as const, label: "Administrador", icon: ShieldCheck },
    lawyer: { variant: "info" as const, label: "Advogado", icon: UserCog },
    paralegal: { variant: "neutral" as const, label: "Paralegal", icon: UserCog },
  };
  const c = config[role];
  const Icon = c.icon;
  return (
    <Badge variant={c.variant}>
      <Icon className="h-2.5 w-2.5" />
      {c.label}
    </Badge>
  );
}

function statusBadge(u: User) {
  if (u.deletedAt) {
    return (
      <Badge variant="outline">
        <Trash2 className="h-2.5 w-2.5" />
        Removido
      </Badge>
    );
  }
  if (u.lockedUntil && new Date(u.lockedUntil) > new Date()) {
    return (
      <Badge variant="danger">
        <Lock className="h-2.5 w-2.5" />
        Bloqueado
      </Badge>
    );
  }
  return (
    <Badge variant="success">
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-success)]" />
      Ativo
    </Badge>
  );
}

function AdminUsersPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: fetchUsers });

  const [showForm, setShowForm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [lastCreated, setLastCreated] = useState<{ email: string; senha: string } | null>(null);

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Usuário removido.");
      setUserToDelete(null);
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const users = data?.users ?? [];

  return (
    <AppShell
      pageEyebrow="Administração"
      pageTitle="Usuários do escritório"
      pageActions={
        <Button onClick={() => setShowForm(true)}>
          <UserPlus className="h-4 w-4" />
          Criar usuário
        </Button>
      }
    >
      {lastCreated && (
        <CredentialsBanner
          email={lastCreated.email}
          senha={lastCreated.senha}
          onDismiss={() => setLastCreated(null)}
        />
      )}

      <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
        <div className="overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-[2fr_1.4fr_130px_120px_110px_56px] gap-3 px-4 py-3 text-label text-[var(--color-ink-muted)] hairline-b bg-[var(--color-canvas)]">
              <span>Usuário</span>
              <span>Cargo · OAB</span>
              <span>Cadastrado em</span>
              <span>Último acesso</span>
              <span>Situação</span>
              <span className="text-right">Ações</span>
            </div>

            {isLoading ? (
              <div className="flex items-center gap-3 px-4 py-10 text-sm text-[var(--color-ink-muted)]">
                <Spinner size="sm" />
                Carregando usuários…
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-md hairline bg-[var(--color-canvas)] text-[var(--color-ink-subtle)] mb-4">
                  <UserCog className="h-6 w-6" />
                </div>
                <h3 className="text-title text-base text-[var(--color-ink)]">
                  Nenhum usuário ainda
                </h3>
                <p className="text-sm text-[var(--color-ink-muted)] mt-2 max-w-xs">
                  Crie o primeiro usuário do escritório para começar a usar o sistema.
                </p>
                <Button variant="secondary" className="mt-5" onClick={() => setShowForm(true)}>
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Criar usuário
                </Button>
              </div>
            ) : (
              <ul>
                {users.map((u) => {
                  const isRemoved = !!u.deletedAt;
                  return (
                    <li
                      key={u.id}
                      className={cn(
                        "group grid grid-cols-[2fr_1.4fr_130px_120px_110px_56px] items-center gap-3 px-4 py-3 hairline-b last:border-b-0 transition-colors",
                        isRemoved
                          ? "opacity-60"
                          : "hover:bg-[var(--color-surface-2)] hover:shadow-[inset_2px_0_0_var(--color-gold)]",
                      )}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          aria-hidden
                          className="shrink-0 flex h-9 w-9 items-center justify-center rounded-sm bg-[var(--color-ink)] text-xs text-mono font-semibold text-[var(--color-surface)]"
                        >
                          {initials(u.name || u.email)}
                        </span>
                        <div className="min-w-0">
                          <p
                            className={cn(
                              "text-sm text-[var(--color-ink)] truncate font-medium",
                              isRemoved && "line-through",
                            )}
                          >
                            {u.name || u.email.split("@")[0]}
                          </p>
                          <p className="text-mono text-xs text-[var(--color-ink-muted)] truncate mt-0.5">
                            {u.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1 min-w-0">
                        {roleBadge(u.role)}
                        {u.oabNumber && (
                          <span className="text-mono text-xs text-[var(--color-ink-muted)] uppercase tracking-wider font-semibold">
                            OAB {u.oabNumber}
                          </span>
                        )}
                      </div>

                      <span className="text-sm text-[var(--color-ink-muted)]">
                        {formatDateBR(u.createdAt)}
                      </span>

                      <span className="text-sm text-[var(--color-ink-muted)]">
                        {formatLastLogin(u.lastLoginAt)}
                      </span>

                      <div>{statusBadge(u)}</div>

                      <div className="flex justify-end">
                        {!isRemoved && (
                          <button
                            type="button"
                            onClick={() => setUserToDelete(u)}
                            aria-label={`Remover usuário ${u.email}`}
                            title={`Remover ${u.email}`}
                            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity rounded-sm p-2 text-[var(--color-ink-muted)] hover:bg-[var(--color-danger-wash)] hover:text-[var(--color-danger)] focus-ring"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <CreateUserDialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          createMut.reset();
        }}
        onSubmit={(input) => {
          createMut.mutate(input, {
            onSuccess: () => {
              setLastCreated({ email: input.email, senha: input.senha });
              setShowForm(false);
              createMut.reset();
            },
          });
        }}
        pending={createMut.isPending}
        error={createMut.error ? (createMut.error as Error).message : null}
      />

      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="text-mono text-[var(--color-ink)]">{userToDelete?.email}</span> será
              removido e suas sessões ativas serão revogadas. A ação fica registrada no audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              onClick={() => {
                if (userToDelete) deleteMut.mutate(userToDelete.id);
              }}
            >
              {deleteMut.isPending ? <Spinner size="sm" /> : "Remover usuário"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function initials(s: string): string {
  const cleaned = s.includes("@") ? s.split("@")[0]! : s;
  const parts = cleaned.split(/[\s._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "??").toUpperCase();
}

function CredentialsBanner({
  email,
  senha,
  onDismiss,
}: {
  email: string;
  senha: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    void navigator.clipboard.writeText(`Email: ${email}\nSenha: ${senha}`).then(() => {
      setCopied(true);
      toast.success("Credenciais copiadas.");
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <div className="mb-5 rounded-md border border-[color:color-mix(in_srgb,var(--color-success)_40%,transparent)] bg-[var(--color-success-wash)] overflow-hidden fade-up">
      <div className="flex items-start gap-3 px-4 py-3 border-l-2 border-l-[var(--color-success)]">
        <KeyRound className="h-4 w-4 mt-1 text-[var(--color-success)] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--color-ink)]">Usuário criado com sucesso</p>
          <p className="text-sm text-[var(--color-ink-muted)] mt-1">
            Compartilhe estas credenciais com o novo usuário agora. A senha não será exibida
            novamente.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-sm hairline bg-[var(--color-surface)] px-2.5 py-1.5">
              <span className="text-mono text-xs uppercase tracking-wider text-[var(--color-ink-muted)] font-semibold">
                Email
              </span>
              <span className="text-mono text-xs text-[var(--color-ink)]">{email}</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-sm hairline bg-[var(--color-surface)] px-2.5 py-1.5">
              <span className="text-mono text-xs uppercase tracking-wider text-[var(--color-ink-muted)] font-semibold">
                Senha
              </span>
              <span className="text-mono text-xs text-[var(--color-ink)]">{senha}</span>
            </div>
            <Button variant="secondary" size="sm" onClick={copy}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copiado
                </>
              ) : (
                <>
                  <Clipboard className="h-3.5 w-3.5" />
                  Copiar
                </>
              )}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-sm p-1 text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink)]"
          aria-label="Dispensar"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateUserDialog({
  open,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateUserInput) => void;
  pending: boolean;
  error: string | null;
}) {
  const [form, setForm] = useState({
    email: "",
    nome: "",
    role: "lawyer" as "admin" | "lawyer" | "paralegal",
    senha: generateTempPassword(),
    oabNumber: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        email: "",
        nome: "",
        role: "lawyer",
        senha: generateTempPassword(),
        oabNumber: "",
      });
    }
  }, [open]);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      email: form.email.trim(),
      nome: form.nome.trim(),
      role: form.role,
      senha: form.senha,
      oabNumber: form.oabNumber.trim() || undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar usuário</DialogTitle>
          <DialogDescription>
            Defina email e senha inicial. O usuário poderá trocar a senha após o primeiro login.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cu-nome">Nome</Label>
              <Input
                id="cu-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="João Silva"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cu-email">Email</Label>
              <Input
                id="cu-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="joao@escritorio.com.br"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cargo</Label>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { v: "admin", label: "Admin", desc: "acesso total" },
                  { v: "lawyer", label: "Advogado", desc: "OAB obrigatório" },
                  { v: "paralegal", label: "Paralegal", desc: "estagiário" },
                ] as const
              ).map((r) => {
                const active = form.role === r.v;
                return (
                  <button
                    key={r.v}
                    type="button"
                    onClick={() => setForm({ ...form, role: r.v })}
                    className={cn(
                      "flex flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-all focus-ring",
                      active
                        ? "border-[var(--color-gold)] bg-[var(--color-gold-wash)] shadow-[var(--shadow-soft)]"
                        : "border-[var(--color-line)] bg-[var(--color-surface)] hover:border-[var(--color-line-strong)]",
                    )}
                  >
                    <span
                      className={cn(
                        "text-xs font-medium",
                        active ? "text-[var(--color-gold)]" : "text-[var(--color-ink)]",
                      )}
                    >
                      {r.label}
                    </span>
                    <span className="text-xs uppercase tracking-wider text-[var(--color-ink-muted)] mt-0.5">
                      {r.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {form.role === "lawyer" && (
            <div className="space-y-2">
              <Label htmlFor="cu-oab">OAB (opcional)</Label>
              <Input
                id="cu-oab"
                placeholder="SP123456"
                value={form.oabNumber}
                onChange={(e) => setForm({ ...form, oabNumber: e.target.value.toUpperCase() })}
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="cu-senha">Senha inicial</Label>
              <button
                type="button"
                onClick={() => setForm({ ...form, senha: generateTempPassword() })}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-gold)] hover:text-[var(--color-gold-bright)] transition-colors focus-ring rounded-sm px-1"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                Gerar nova
              </button>
            </div>
            <Input
              id="cu-senha"
              type="text"
              value={form.senha}
              onChange={(e) => setForm({ ...form, senha: e.target.value })}
              className="text-mono"
              required
            />
            <p className="text-xs text-[var(--color-ink-muted)] leading-relaxed">
              Mínimo de 12 caracteres, com letras maiúsculas e minúsculas, números e símbolos.
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-[color:color-mix(in_srgb,var(--color-danger)_40%,transparent)] bg-[var(--color-danger-wash)] px-3 py-2 text-sm text-[var(--color-danger)]">
              {error}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? (
                <>
                  <Spinner size="sm" />
                  Criando…
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  Criar usuário
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
